import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FORMAT_OPTIONS, GENRE_OPTIONS } from '../../features/writerProfile/options';

const FORMAT_DESCRIPTIONS: Record<string, { key: string; label: string }> = {
  novel: { key: 'stories.formatDesc.novel', label: 'Full-length book' },
  short_story: { key: 'stories.formatDesc.shortStory', label: 'Compact narrative' },
  screenplay: { key: 'stories.formatDesc.screenplay', label: 'Script format' },
  stage_play: { key: 'stories.formatDesc.stagePlay', label: 'For the theatre' },
  fan_fiction: { key: 'stories.formatDesc.fanFiction', label: 'Inspired by existing works' },
  poetry: { key: 'stories.formatDesc.poetry', label: 'Verse collection' },
};

const toneOptions = ['Dark', 'Light', 'Humorous', 'Dramatic', 'Poetic', 'Gritty', 'Whimsical'];
/** Pick one or more: the first is the primary voice, later picks are mixed-in devices. */
const povOptions = [
  {
    value: 'First Person',
    descKey: 'stories.povDesc.first',
    desc: '\u201cI walked in.\u201d Inside one head \u2014 intimate, biased, limited to what they know.',
  },
  {
    value: 'Third Person Limited',
    descKey: 'stories.povDesc.thirdLimited',
    desc: '\u201cShe walked in.\u201d Follows one character at a time; knows only what they know.',
  },
  {
    value: 'Third Person Omniscient',
    descKey: 'stories.povDesc.thirdOmniscient',
    desc: 'An all-seeing narrator \u2014 every head, every room, free to comment.',
  },
  {
    value: 'Second Person',
    descKey: 'stories.povDesc.second',
    desc: '\u201cYou walk in.\u201d Speaks to the reader or audience \u2014 fourth-wall breaks live here.',
  },
];

/** Length in the format's native unit; words stay the unit under the hood. */
const lengthOptionsFor = (format: string) => {
  if (format === 'screenplay') {
    return [
      {
        value: 'screen_short',
        key: 'stories.length.screenShort',
        label: 'Short film (~15 min \u00b7 ~15 pages)',
      },
      {
        value: 'screen_22',
        key: 'stories.length.screen22',
        label: 'TV episode (22 min \u00b7 ~22 pages)',
      },
      {
        value: 'screen_30',
        key: 'stories.length.screen30',
        label: 'Half hour (30 min \u00b7 ~30 pages)',
      },
      {
        value: 'screen_60',
        key: 'stories.length.screen60',
        label: 'One hour (60 min \u00b7 ~55-60 pages)',
      },
      {
        value: 'screen_90',
        key: 'stories.length.screen90',
        label: 'Feature (90 min \u00b7 ~90 pages)',
      },
      {
        value: 'screen_120',
        key: 'stories.length.screen120',
        label: 'Feature (2 h \u00b7 ~120 pages)',
      },
      {
        value: 'screen_150',
        key: 'stories.length.screen150',
        label: 'Epic feature (2.5 h \u00b7 ~150 pages)',
      },
    ];
  }
  if (format === 'stage_play') {
    return [
      { value: 'stage_10', key: 'stories.length.stage10', label: 'Ten-minute play (~10 pages)' },
      {
        value: 'stage_30',
        key: 'stories.length.stage30',
        label: 'One-act (~30 min \u00b7 ~30 pages)',
      },
      {
        value: 'stage_60',
        key: 'stories.length.stage60',
        label: 'Long one-act (~60 min \u00b7 ~60 pages)',
      },
      {
        value: 'stage_90',
        key: 'stories.length.stage90',
        label: 'Full-length (~90 min \u00b7 ~90 pages)',
      },
      {
        value: 'stage_120',
        key: 'stories.length.stage120',
        label: 'Two-act evening (~2 h \u00b7 ~120 pages)',
      },
    ];
  }
  return [
    {
      value: 'short',
      key: 'stories.length.short',
      label: 'Short (~5K words \u00b7 ~20 pages \u00b7 a 20-min read)',
    },
    {
      value: 'medium',
      key: 'stories.length.medium',
      label: 'Novella (~20K words \u00b7 ~80 pages \u00b7 a 1.5-h read)',
    },
    {
      value: 'novel',
      key: 'stories.length.novel',
      label: 'Novel (60K+ words \u00b7 ~250 pages \u00b7 a 4-5-h read)',
    },
    {
      value: 'epic',
      key: 'stories.length.epic',
      label: 'Epic (100K+ words \u00b7 ~400 pages \u00b7 an 8-h read)',
    },
  ];
};

const CreateStory: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState('novel');
  const [genres, setGenres] = useState<string[]>([]);
  const [tone, setTone] = useState('');
  const [povs, setPovs] = useState<string[]>([]);
  const [targetLength, setTargetLength] = useState('');
  useEffect(() => {
    setTargetLength((v) => (v && !lengthOptionsFor(format).some((o) => o.value === v) ? '' : v));
  }, [format]);
  const [submitting, setSubmitting] = useState(false);

  const toggleGenre = (genre: string) => {
    setGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre],
    );
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error(t('stories.titleRequired', 'Please enter a title'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post('/stories', {
        title: title.trim(),
        format,
        genres,
        tone: tone || undefined,
        pov: povs.length ? povs.join(' + ') : undefined,
        targetLength: targetLength || undefined,
      });
      navigate(`/stories/${res.data.id}`);
    } catch (error) {
      toast.error(t('stories.createError', 'Failed to create story'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileLayout headerTitle={t('stories.createStory', 'New Story')} showBackButton>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Title */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.titleLabel', 'Title')}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('stories.titlePlaceholder', 'Your story title...')}
            className="w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
            style={{
              borderColor: 'var(--ds-border)',
              color: 'var(--ds-text-primary)',
            }}
          />
        </div>

        {/* Format */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.formatLabel', 'Format')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {FORMAT_OPTIONS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFormat(f.value)}
                className={`p-3 rounded-xl border text-left transition-all ${
                  format === f.value ? 'ring-2 ring-primary-500' : ''
                }`}
                style={{
                  borderColor:
                    format === f.value ? 'var(--ds-accent, #8b5cf6)' : 'var(--ds-border)',
                  background: format === f.value ? 'rgba(139,92,246,0.1)' : 'var(--ds-bg-card)',
                }}
              >
                <div className="font-medium text-sm" style={{ color: 'var(--ds-text-primary)' }}>
                  {f.emoji} {t(f.key, f.label)}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--ds-text-secondary)' }}>
                  {t(FORMAT_DESCRIPTIONS[f.value].key, FORMAT_DESCRIPTIONS[f.value].label)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Genres */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.genreLabel', 'Genres')}
          </label>
          <div className="flex flex-wrap gap-2">
            {GENRE_OPTIONS.map(({ value: genre, key, label }) => (
              <button
                key={genre}
                onClick={() => toggleGenre(genre)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  genres.includes(genre) ? 'bg-primary-500 text-white' : ''
                }`}
                style={
                  !genres.includes(genre)
                    ? {
                        background: 'var(--ds-bg-card)',
                        border: '1px solid var(--ds-border)',
                        color: 'var(--ds-text-secondary)',
                      }
                    : {}
                }
              >
                {t(key, label)}
              </button>
            ))}
          </div>
        </div>

        {/* Tone */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.toneLabel', 'Tone')}
          </label>
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
            style={{
              borderColor: 'var(--ds-border)',
              color: 'var(--ds-text-primary)',
            }}
          >
            <option value="">{t('stories.selectTone', 'Select tone...')}</option>
            {toneOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        {/* POV */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.povLabel', 'Point of View')}
          </label>
          <p className="text-xs mb-2" style={{ color: 'var(--ds-text-secondary)' }}>
            {t(
              'stories.povHint',
              'Pick one or more \u2014 your first pick is the main voice, extras are mixed in (e.g. omniscient narration with fourth-wall asides).',
            )}
          </p>
          <div className="space-y-2">
            {povOptions.map((o) => {
              const idx = povs.indexOf(o.value);
              const selected = idx >= 0;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() =>
                    setPovs((prev) =>
                      prev.includes(o.value)
                        ? prev.filter((v) => v !== o.value)
                        : [...prev, o.value],
                    )
                  }
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${selected ? 'border-primary-500 ring-1 ring-primary-500' : 'hover:border-primary-400'}`}
                  style={{
                    borderColor: selected ? undefined : 'var(--ds-border)',
                    background: selected ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                  }}
                >
                  <span className="flex items-center justify-between">
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--ds-text-primary)' }}
                    >
                      {o.value}
                    </span>
                    {selected && (
                      <span className="text-xs font-semibold text-primary-400">
                        {idx === 0
                          ? t('stories.povPrimary', 'Primary')
                          : t('stories.povMixedIn', 'Mixed in')}
                      </span>
                    )}
                  </span>
                  <span
                    className="block text-xs mt-0.5"
                    style={{ color: 'var(--ds-text-secondary)' }}
                  >
                    {t(o.descKey, o.desc)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Target Length */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {t('stories.lengthLabel', 'Target Length')}
          </label>
          <select
            value={targetLength}
            onChange={(e) => setTargetLength(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
            style={{
              borderColor: 'var(--ds-border)',
              color: 'var(--ds-text-primary)',
            }}
          >
            <option value="">{t('stories.selectLength', 'Select length...')}</option>
            {lengthOptionsFor(format).map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.key, o.label)}
              </option>
            ))}
          </select>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || !title.trim()}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-xl font-semibold transition-colors"
        >
          {submitting
            ? t('stories.creating', 'Creating...')
            : t('stories.createButton', 'Create Story')}
        </button>
      </div>
    </MobileLayout>
  );
};

export default CreateStory;
